import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Users, Bell, User, Settings, LogOut, ChevronDown, UserPlus, MessageCircle, LogIn, X, Shield, Code, Calendar, Check, XCircle, Heart, Menu } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfileByUid, UserProfile, searchEverything, SearchResult, subscribeToNotifications, markNotificationRead as markNotificationReadFirestore, convertTimestampToDate } from '../firebase/firestore';
import { subscribeToFriendRequests, FriendRequest, acceptFriendRequest, declineFriendRequest } from '../firebase/friends';
import { updateUserStatus } from '../firebase/userStatus';
import { playFriendRequestSound, playGeneralNotificationSound, playMentionSound, playMessageSound, setNotificationSoundEnabled, isNotificationSoundEnabled } from '../utils/notificationSound';
import { subscribeToConversations, Conversation } from '../firebase/messaging';
import FriendsDropdown from './FriendsDropdown';
import LanguageSelector from './LanguageSelector';
import ThemeToggle from './ui/ThemeToggle';
import { useAdmin } from '../hooks/useAdmin';
import { useDeveloper } from '../hooks/useDeveloper';
import { useTranslation } from 'react-i18next';
import { formatHandle } from '../utils/formatHandle';
import { getNewNotificationsToPush, requestBrowserNotificationPermission, showBrowserNotification } from '../utils/browserNotifications';

interface HeaderProps {
  user?: any;
  onAuthSuccess?: () => void;
  /** Opens the sidebar drawer on small screens. */
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, onAuthSuccess, onMenuClick }) => {
  const navigate = useNavigate();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'friend_request' | 'message' | 'info' | 'post_like' | 'post_comment' | 'mention';
    title: string;
    message: string;
    timestamp: Date;
    read: boolean;
    friendRequestId?: string;
    fromUserId?: string;
    fromUserName?: string;
    fromUserImageUrl?: string;
    isFirestore?: boolean;
    metadata?: { url?: string };
  }>>([]);
  const [firestoreNotifications, setFirestoreNotifications] = useState<Array<{
    id: string;
    type: 'post_like' | 'post_comment' | 'mention';
    title: string;
    message: string;
    read: boolean;
    createdAt: any;
    metadata?: { url?: string };
  }>>([]);
  const [pendingFriendRequests, setPendingFriendRequests] = useState<FriendRequest[]>([]);
  const [processingRequests, setProcessingRequests] = useState<Set<string>>(new Set());
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [showFriendsDropdown, setShowFriendsDropdown] = useState(false);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [showDeveloperDropdown, setShowDeveloperDropdown] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => isNotificationSoundEnabled());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const previousFriendRequestCount = useRef(0);
  const previousConversationsRef = useRef<Conversation[]>([]);
  const seenNotificationIdsRef = useRef<Set<string>>(new Set());
  const { user: authUser } = useAuth();
  const { isAdminUser } = useAdmin(user?.uid || authUser?.uid || null);
  const { isDeveloperUser } = useDeveloper(user?.uid || authUser?.uid || null);
  const { t } = useTranslation();
  
  // Use passed user prop or fallback to auth context
  const currentUser = user || authUser;
  const currentUserId = currentUser?.uid || null;

  // Notification functions
  const addNotification = useCallback((type: 'friend_request' | 'message' | 'info', title: string, message: string) => {
    const notification = {
      id: Date.now().toString(),
      type,
      title,
      message,
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => [notification, ...prev]);
    
    // Play appropriate sound based on notification type
    if (soundEnabled) {
      switch (type) {
        case 'friend_request':
          playFriendRequestSound();
          break;
        case 'message':
          playMessageSound();
          break;
        default:
          playGeneralNotificationSound();
          break;
      }
    }
  }, [soundEnabled]);

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const markNotificationAsRead = async (id: string) => {
    const isFirestore = firestoreNotifications.some(n => n.id === id);
    if (isFirestore) {
      try {
        await markNotificationReadFirestore(id);
        setFirestoreNotifications(prev =>
          prev.map(n => n.id === id ? { ...n, read: true } : n)
        );
      } catch (e) {
        console.error('Failed to mark notification read', e);
      }
    } else {
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    }
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    for (const n of firestoreNotifications.filter(n => !n.read)) {
      try {
        await markNotificationReadFirestore(n.id);
      } catch (e) {
        console.error('Failed to mark notification read', e);
      }
    }
    setFirestoreNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const toggleSound = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    setNotificationSoundEnabled(newSoundEnabled);
  };

  // Subscribe to Firestore notifications (post like/comment)
  useEffect(() => {
    if (!currentUserId) {
      setFirestoreNotifications([]);
      seenNotificationIdsRef.current = new Set();
      return;
    }
    const unsub = subscribeToNotifications(currentUserId, (list) => {
      const mapped = list.map((n) => ({
        id: n.id,
        type: n.type as 'post_like' | 'post_comment' | 'mention',
        title: n.title,
        message: n.message,
        read: n.read,
        createdAt: n.createdAt,
        metadata: n.metadata
      }));
      setFirestoreNotifications(mapped);

      const newlyUnread = getNewNotificationsToPush(seenNotificationIdsRef.current, mapped);
      if (newlyUnread.length > 0) {
        newlyUnread.forEach((notification) => {
          showBrowserNotification(notification.title || 'New activity', notification.message || 'You have a new notification');
        });
        if (soundEnabled) {
          if (newlyUnread.some((n) => n.type === 'mention')) {
            playMentionSound();
          } else {
            playGeneralNotificationSound();
          }
        }
        newlyUnread.forEach((notification) => seenNotificationIdsRef.current.add(notification.id));
      }
    });
    return () => unsub();
  }, [currentUserId, soundEnabled]);

  useEffect(() => {
    if (!currentUserId) return;
    requestBrowserNotificationPermission().catch(() => undefined);
  }, [currentUserId]);

  const mergedNotifications = [
    ...firestoreNotifications.map((n) => ({
      id: n.id,
      type: n.type,
      title: n.title,
      message: n.message,
      timestamp: convertTimestampToDate(n.createdAt),
      read: n.read,
      isFirestore: true as const,
      metadata: n.metadata
    })),
    ...notifications
  ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const unreadCount = mergedNotifications.filter(n => !n.read).length;

  // Handle accepting friend request from notification
  const handleAcceptFriendRequest = async (notificationId: string, friendRequestId: string) => {
    if (!friendRequestId || processingRequests.has(friendRequestId)) return;
    
    setProcessingRequests(prev => new Set(prev).add(friendRequestId));
    
    try {
      await acceptFriendRequest(friendRequestId);
      // Remove notification after accepting
      removeNotification(notificationId);
      // Remove from pending requests
      setPendingFriendRequests(prev => prev.filter(r => r.id !== friendRequestId));
    } catch (error) {
      console.error('Error accepting friend request:', error);
    } finally {
      setProcessingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(friendRequestId);
        return newSet;
      });
    }
  };

  // Handle declining friend request from notification
  const handleDeclineFriendRequest = async (notificationId: string, friendRequestId: string) => {
    if (!friendRequestId || processingRequests.has(friendRequestId)) return;
    
    setProcessingRequests(prev => new Set(prev).add(friendRequestId));
    
    try {
      await declineFriendRequest(friendRequestId);
      // Remove notification after declining
      removeNotification(notificationId);
      // Remove from pending requests
      setPendingFriendRequests(prev => prev.filter(r => r.id !== friendRequestId));
    } catch (error) {
      console.error('Error declining friend request:', error);
    } finally {
      setProcessingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(friendRequestId);
        return newSet;
      });
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showNotificationDropdown && !target.closest('.notification-dropdown')) {
        setShowNotificationDropdown(false);
      }
      if (showFriendsDropdown && !target.closest('.friends-dropdown')) {
        setShowFriendsDropdown(false);
      }
      if (showSearchDropdown && !target.closest('.search-dropdown')) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationDropdown, showFriendsDropdown, showSearchDropdown]);

  // Handle search with debouncing - search everything
  useEffect(() => {
    if (!currentUserId || !searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchEverything(searchQuery, currentUserId);
        setSearchResults(results);
        setShowSearchDropdown(true);
      } catch (error) {
        console.error('Error searching:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, currentUserId]);

  // Subscribe to friend requests for notifications
  useEffect(() => {
    if (!currentUserId) return;

    const unsubscribeRequests = subscribeToFriendRequests(currentUserId, (requests) => {
      // Store pending friend requests
      setPendingFriendRequests(requests);
      
      if (requests.length > previousFriendRequestCount.current) {
        const newRequest = requests[0];
        if (newRequest) {
          // Add notification with friend request data
          const notification = {
            id: Date.now().toString(),
            type: 'friend_request' as const,
            title: t('common.newFriendRequest'),
            message: `${newRequest.fromUserName} ${t('common.wantsToBeFriend')}`,
            timestamp: new Date(),
            read: false,
            friendRequestId: newRequest.id,
            fromUserId: newRequest.fromUserId,
            fromUserName: newRequest.fromUserName,
            fromUserImageUrl: newRequest.fromUserImageUrl
          };
          setNotifications(prev => [notification, ...prev]);
          
          // Play sound
          if (soundEnabled) {
            playFriendRequestSound();
          }
        }
      }
      previousFriendRequestCount.current = requests.length;
    });

    return () => unsubscribeRequests();
  }, [currentUserId, soundEnabled, t]);

  // Subscribe to conversations for message notifications
  useEffect(() => {
    if (!currentUserId) return;

    const unsubscribeConversations = subscribeToConversations(currentUserId, (conversations) => {
      conversations.forEach((conv) => {
        const unreadCount = conv.unreadCount[currentUserId] || 0;

        if (unreadCount > 0 && conv.lastMessage && conv.lastMessageSender !== currentUserId) {
          const prevConv = previousConversationsRef.current.find(c => c.id === conv.id);
          const prevUnreadCount = prevConv?.unreadCount[currentUserId] || 0;

          if (unreadCount > prevUnreadCount) {
            const senderName = conv.participantNames[conv.lastMessageSender || ''] || 'Someone';

            addNotification(
              'message',
              t('common.newMessage'),
              `${senderName}: ${conv.lastMessage.substring(0, 50)}${conv.lastMessage.length > 50 ? '...' : ''}`
            );
          }
        }
      });

      previousConversationsRef.current = conversations;
    });

    return () => unsubscribeConversations();
  }, [currentUserId, addNotification]);

  // Manage current user's online status
  useEffect(() => {
    if (!currentUserId) return;

    updateUserStatus(currentUserId, true, 'online');

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        updateUserStatus(currentUserId, false, 'away');
      } else {
        updateUserStatus(currentUserId, true, 'online');
      }
    };

    // Handle page unload (user closes tab/browser)
    const handleBeforeUnload = () => {
      updateUserStatus(currentUserId, false, 'offline');
    };

    // Handle window focus/blur
    const handleFocus = () => {
      updateUserStatus(currentUserId, true, 'online');
    };

    const handleBlur = () => {
      updateUserStatus(currentUserId, false, 'away');
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Cleanup function
    return () => {
      // Set user as offline when component unmounts
      updateUserStatus(currentUserId, false, 'offline');
      
      // Remove event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [currentUserId]);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!currentUserId) {
        setUserProfile(null);
        setLoading(false);
        return;
      }

      try {
        const profile = await getUserProfileByUid(currentUserId);
        setUserProfile(profile);
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [currentUserId]);

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setIsProfileDropdownOpen(false);
      if (onAuthSuccess) {
        onAuthSuccess();
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="glass-subtle relative z-30 border-b border-dark-border/60 px-3 sm:px-4 md:px-6 py-2.5">
      <div className="flex items-center justify-between gap-2">
        {/* Mobile: open navigation drawer */}
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open menu"
          className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer">
          <img
            src="/Official Logo (no background).PNG"
            alt="The Kinship Way"
            className="h-8 w-auto"
          />
          <span className="hidden sm:block text-lg font-extrabold tracking-tight text-text-primary">
            thekinshipway
          </span>
        </Link>

        {/* Universal search */}
        <div className="flex-1 max-w-xl mx-1 md:mx-6 hidden md:block search-dropdown">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim().length >= 2) {
                  setShowSearchDropdown(true);
                }
              }}
              onFocus={() => {
                if (searchQuery.trim().length >= 2 && searchResults.length > 0) {
                  setShowSearchDropdown(true);
                }
              }}
              className="w-full rounded-full border border-dark-border/70 bg-surface-sunken/70 pl-11 pr-4 py-2.5 text-sm text-text-primary placeholder-text-muted transition-all duration-200 ease-premium focus:outline-none focus:border-accent-blue/60 focus:bg-dark-card focus:shadow-glow-blue"
            />

            {/* Search Results Dropdown */}
            {showSearchDropdown && (searchQuery.trim().length >= 2) && (
              <div className="absolute top-full left-0 right-0 mt-2 glass rounded-2xl shadow-elevated z-50 max-h-96 overflow-y-auto">
                {isSearching ? (
                  <div className="p-4 text-center text-text-muted">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-blue mx-auto mb-2"></div>
                    <p className="text-sm">{t('common.searching')}</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="py-2">
                    {/* Group results by type */}
                    {['user', 'post', 'event', 'chatroom'].map((type) => {
                      const typeResults = searchResults.filter(r => r.type === type);
                      if (typeResults.length === 0) return null;

                      const typeLabels = {
                        user: t('common.users'),
                        post: t('common.posts'),
                        event: t('common.events'),
                        chatroom: t('common.topics')
                      };

                      const typeIcons = {
                        user: User,
                        post: MessageCircle,
                        event: Calendar,
                        chatroom: Users
                      };

                      const Icon = typeIcons[type as keyof typeof typeIcons];

                      return (
                        <div key={type} className="mb-2">
                          <div className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wide border-b border-dark-border">
                            {typeLabels[type as keyof typeof typeLabels]}
                          </div>
                          {typeResults.map((result) => (
                            <Link
                              key={`${result.type}-${result.id}`}
                              to={result.url}
                              onClick={() => {
                                setShowSearchDropdown(false);
                                setSearchQuery('');
                              }}
                              className="flex items-center px-4 py-3 hover:bg-dark-card transition-colors"
                            >
                              {result.imageUrl ? (
                                <img
                                  src={result.imageUrl}
                                  alt={result.title}
                                  className={`${result.type === 'user' ? 'w-10 h-10 rounded-full' : 'w-12 h-12 rounded'} object-cover mr-3`}
                                />
                              ) : (
                                <div className={`${result.type === 'user' ? 'w-10 h-10 rounded-full' : 'w-12 h-12 rounded'} bg-accent-blue flex items-center justify-center mr-3`}>
                                  <Icon className="w-5 h-5 text-white" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-text-primary truncate">
                                  {result.title}
                                </p>
                                {result.description && (
                                  <p className="text-xs text-text-muted truncate mt-1">
                                    {result.description}
                                  </p>
                                )}
                                {result.metadata?.handle && (
                                  <p className="text-xs text-text-muted truncate">
                                    {formatHandle(result.metadata.handle)}
                                  </p>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 text-center text-text-muted">
                    <p className="text-sm">{t('common.noResultsFound')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Side Icons - More compact on mobile */}
        <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Language Selector */}
          <LanguageSelector />

          {currentUser ? (
            <>
              {/* Friends Dropdown */}
              <div className="relative friends-dropdown">
                <button 
                  onClick={() => {
                    setShowFriendsDropdown(!showFriendsDropdown);
                    setShowNotificationDropdown(false);
                    setShowAdminDropdown(false);
                  }}
                  className="p-2 text-text-muted hover:text-text-primary transition-colors relative"
                >
                  <Users className="w-5 h-5" />
                </button>
                <FriendsDropdown 
                  userId={currentUser.uid} 
                  isOpen={showFriendsDropdown}
                  onClose={() => setShowFriendsDropdown(false)}
                />
              </div>

              {/* Admin Dropdown - Only show for admins */}
              {isAdminUser && (
                <div className="relative admin-dropdown">
                  <button 
                    onClick={() => {
                      setShowAdminDropdown(!showAdminDropdown);
                      setShowNotificationDropdown(false);
                      setShowFriendsDropdown(false);
                      setShowDeveloperDropdown(false);
                    }}
                    className="p-2 text-text-muted hover:text-text-primary transition-colors relative"
                  >
                    <Shield className="w-5 h-5" />
                  </button>
                  {showAdminDropdown && (
                    <div className="absolute top-12 right-0 w-48 md:w-56 glass rounded-2xl shadow-elevated overflow-hidden z-50">
                      <div className="py-1">
                        <Link 
                          to="/admin-panel" 
                          className="flex items-center px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-card transition-colors"
                          onClick={() => setShowAdminDropdown(false)}
                        >
                          <Shield className="w-4 h-4 mr-3" />
                          {t('common.adminPanel')}
                        </Link>
                        <Link 
                          to="/admin-management" 
                          className="flex items-center px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-card transition-colors"
                          onClick={() => setShowAdminDropdown(false)}
                        >
                          <Users className="w-4 h-4 mr-3" />
                          {t('common.manageAdmins')}
                        </Link>
                        <Link 
                          to="/admin-setup" 
                          className="flex items-center px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-card transition-colors"
                          onClick={() => setShowAdminDropdown(false)}
                        >
                          <Settings className="w-4 h-4 mr-3" />
                          {t('common.adminSetup')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Developer Dropdown - Only show for developers */}
              {isDeveloperUser && (
                <div className="relative developer-dropdown">
                  <button 
                    onClick={() => {
                      setShowDeveloperDropdown(!showDeveloperDropdown);
                      setShowNotificationDropdown(false);
                      setShowFriendsDropdown(false);
                      setShowAdminDropdown(false);
                    }}
                    className="p-2 text-text-muted hover:text-text-primary transition-colors relative"
                  >
                    <Code className="w-5 h-5" />
                  </button>
                  {showDeveloperDropdown && (
                    <div className="absolute top-12 right-0 w-48 md:w-56 glass rounded-2xl shadow-elevated overflow-hidden z-50">
                      <div className="py-1">
                        <Link 
                          to="/developer-panel" 
                          className="flex items-center px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-card transition-colors"
                          onClick={() => setShowDeveloperDropdown(false)}
                        >
                          <Code className="w-4 h-4 mr-3" />
                          {t('common.developerPanel')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
          {/* Notification Bell */}
          <div className="relative notification-dropdown">
            <button
              onClick={() => {
                setShowNotificationDropdown(!showNotificationDropdown);
                setShowFriendsDropdown(false);
                setShowAdminDropdown(false);
                setShowDeveloperDropdown(false);
              }}
              className="relative p-2 text-text-muted hover:text-text-primary transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}
            </button>

            {/* Notification Dropdown */}
            {showNotificationDropdown && (
              <div className="absolute top-12 right-0 w-[90vw] md:w-80 glass rounded-2xl shadow-elevated max-h-96 overflow-hidden z-50">
                {/* Header */}
                <div className="p-4 border-b border-dark-border">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-text-primary">{t('common.notifications')}</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-sm text-accent-blue hover:text-accent-blue transition-colors"
                      >
                        {t('common.markAllAsRead')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Notifications List */}
                <div className="max-h-80 overflow-y-auto">
                  {mergedNotifications.length === 0 ? (
                    <div className="p-4 text-center text-text-muted">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>{t('common.noNotifications')}</p>
                    </div>
                  ) : (
                    mergedNotifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 border-b border-dark-border hover:bg-dark-card transition-colors ${
                          !notification.read ? 'bg-accent-blue-strong/10' : ''
                        }`}
                        onClick={() => markNotificationAsRead(notification.id)}
                      >
                        <div className="flex items-start space-x-3">
                          {/* Avatar for friend requests */}
                          {notification.type === 'friend_request' && notification.fromUserImageUrl ? (
                            <img 
                              src={notification.fromUserImageUrl} 
                              alt={notification.fromUserName || 'User'}
                              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className={`p-2 rounded-full flex-shrink-0 ${
                              notification.type === 'friend_request' 
                                ? 'bg-accent-blue text-white' 
                                : notification.type === 'message'
                                ? 'bg-green-500 text-white'
                                : notification.type === 'post_like'
                                ? 'bg-rose-500 text-white'
                                : notification.type === 'post_comment'
                                ? 'bg-accent-blue text-white'
                                : notification.type === 'mention'
                                ? 'bg-accent-purple text-white'
                                : 'bg-yellow-500 text-white'
                            }`}>
                              {notification.type === 'friend_request' ? (
                                <UserPlus className="w-4 h-4" />
                              ) : notification.type === 'message' ? (
                                <MessageCircle className="w-4 h-4" />
                              ) : notification.type === 'post_like' ? (
                                <Heart className="w-4 h-4" />
                              ) : notification.type === 'post_comment' ? (
                                <MessageCircle className="w-4 h-4" />
                              ) : notification.type === 'mention' ? (
                                <span className="w-4 h-4 flex items-center justify-center text-xs font-bold">@</span>
                              ) : (
                                <Bell className="w-4 h-4" />
                              )}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className={`text-sm font-semibold ${
                                !notification.read ? 'text-text-primary' : 'text-text-secondary'
                              }`}>
                                {notification.title}
                              </h4>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-text-muted">
                                  {notification.timestamp.toLocaleTimeString()}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (notification.isFirestore) {
                                      setFirestoreNotifications(prev => prev.filter(n => n.id !== notification.id));
                                    } else {
                                      removeNotification(notification.id);
                                    }
                                  }}
                                  className="text-text-muted hover:text-text-primary transition-colors"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <p className={`text-xs mt-1 ${
                              !notification.read ? 'text-text-secondary' : 'text-text-muted'
                            }`}>
                              {notification.message}
                            </p>
                            {(notification.type === 'post_like' || notification.type === 'post_comment' || notification.type === 'mention') && notification.metadata?.url && (
                              <Link
                                to={notification.metadata.url}
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-accent-blue hover:underline mt-1 inline-block"
                              >
                                View post →
                              </Link>
                            )}
                            
                            {/* Accept/Decline buttons for friend requests */}
                            {notification.type === 'friend_request' && notification.friendRequestId && (
                              <div className="flex items-center space-x-2 mt-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAcceptFriendRequest(notification.id, notification.friendRequestId!);
                                  }}
                                  disabled={processingRequests.has(notification.friendRequestId)}
                                  className="flex items-center space-x-1 px-3 py-1.5 bg-accent-green text-white text-xs font-medium rounded-md hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Check className="w-3 h-3" />
                                  <span>{t('common.accept')}</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeclineFriendRequest(notification.id, notification.friendRequestId!);
                                  }}
                                  disabled={processingRequests.has(notification.friendRequestId)}
                                  className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <XCircle className="w-3 h-3" />
                                  <span>{t('common.decline')}</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Profile Dropdown */}
          <div className="relative">
            <button
              onClick={toggleProfileDropdown}
              className="flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-surface-sunken"
            >
              <div className="relative">
                {loading ? (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-dark-card rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-accent-blue"></div>
                  </div>
                ) : userProfile?.profileImageUrl ? (
                  <img 
                    src={userProfile.profileImageUrl} 
                    alt={userProfile.displayName}
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-accent-blue rounded-full flex items-center justify-center">
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                )}
                {/* Online Status Indicator */}
                <div className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full border-2 border-dark-bg"></div>
              </div>
              <span className="hidden md:block max-w-[110px] truncate text-sm font-semibold text-text-primary">
                {(userProfile?.displayName || user?.displayName || '').split(' ')[0]}
              </span>
              <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-text-muted" />
            </button>
            
            {/* Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-52 glass rounded-2xl shadow-elevated overflow-hidden z-50">
                <div className="py-1">
                  <div className="px-4 py-2 border-b border-dark-border">
                    <p className="text-sm font-medium text-text-primary">
                      {userProfile?.displayName || user?.displayName || 'User'}
                    </p>
                    <p className="text-xs text-text-muted">
                      {userProfile?.handle ? formatHandle(userProfile.handle) : user?.email}
                    </p>
                  </div>
                  
                  <Link 
                    to="/profile" 
                    className="flex items-center px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-bg transition-colors"
                    onClick={() => setIsProfileDropdownOpen(false)}
                  >
                    <User className="w-4 h-4 mr-3" />
                    {t('common.viewProfile')}
                  </Link>
                  <Link to="/earn-rewards" className="flex items-center px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-bg transition-colors" onClick={() => setIsProfileDropdownOpen(false)}><Users className="w-4 h-4 mr-3" />Earn Rewards</Link>
                  
                  <Link 
                    to="/profile" 
                    className="flex items-center px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-bg transition-colors"
                    onClick={() => setIsProfileDropdownOpen(false)}
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    {t('common.settings')}
                  </Link>
                  
                  <button 
                    onClick={() => {
                      toggleSound();
                      setIsProfileDropdownOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-bg transition-colors"
                  >
                    <Bell className="w-4 h-4 mr-3" />
                    {soundEnabled ? t('common.disableSounds') : t('common.enableSounds')}
                  </button>
                  
                  <div className="border-t border-dark-border my-1"></div>
                  
                  <button 
                    onClick={handleSignOut}
                    className="flex items-center w-full px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-dark-bg transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    {t('common.signOut')}
                  </button>
                </div>
              </div>
            )}
          </div>
            </>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="flex items-center space-x-2 bg-accent-blue text-white px-4 py-2 rounded-md hover:bg-accent-blue transition-colors"
            >
              <LogIn className="w-4 h-4" />
              <span>{t('common.signIn')}</span>
            </button>
          )}
        </div>
      </div>

    </header>
  );
};

export default Header;
